"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface AccordionStep {
  id: string;
  label: string;
  icon: string;
  headline: string;
  bullets: string[];
}

const steps: AccordionStep[] = [
  {
    id: "context",
    label: "Context",
    icon: "DatabaseIcon",
    headline: "Context: Map Systems, Define Process Scope, Identify Data Ownership",
    bullets: [
      "Map all source/target systems (ERP, WMS, CRM, etc.)",
      "Define business process boundaries (where does this integration start/end?)",
      "Identify data ownership (who is the system of record?)",
      "Document current state vs. desired state",
    ],
  },
  {
    id: "impact",
    label: "Impact",
    icon: "BoltIcon",
    headline: "Impact: What Breaks if This Integration Fails?",
    bullets: [
      "Financial: Incorrect GL postings, revenue recognition errors",
      "Compliance: Tax reporting failures, audit trail gaps",
      "Operational: Order fulfillment delays, inventory mismatches",
      "Reputational: Customer trust, partner relationships",
    ],
  },
  {
    id: "differentiation",
    label: "Differentiation",
    icon: "LightBulbIcon",
    headline: "Differentiation: Why Is This Integration Complex?",
    bullets: [
      "ERP-specific data models (SAP IDoc vs. Oracle XML vs. NetSuite REST)",
      "Real-time vs. batch trade-offs (latency, throughput, cost)",
      "Partner network constraints (EDI 810/850, cXML, SWIFT)",
      "Legacy system limitations (no APIs, file-based only)",
    ],
  },
  {
    id: "illustration",
    label: "Illustration/Demo",
    icon: "PresentationChartLineIcon",
    headline: "Illustration: Show, Don't Tell",
    bullets: [
      "Sample payload structures (JSON, XML, EDI segments)",
      "Flow diagrams (happy path, error handling, retry logic)",
      "Reference architecture (logical + physical layers)",
      "Demo with your Sandbox connectivity (prove it works)",
    ],
  },
  {
    id: "flow",
    label: "Flow",
    icon: "ArrowPathIcon",
    headline: "Flow: End-to-End Steps with Error Handling",
    bullets: [
      "Inbound: Receive, validate, enrich, transform",
      "Processing: Business logic, orchestration, state management",
      "Outbound: Deliver, confirm, reconcile, audit",
      "Error handling: Circuit breakers, dead letter queues, alerts",
    ],
  },
  {
    id: "leverage",
    label: "Leverage",
    icon: "CubeIcon",
    headline: "Leverage: Reusable Assets & Patterns",
    bullets: [
      "Standard patterns library (batch, real-time, event-driven)",
      "Pre-built connectors (SAP, Oracle, NetSuite, Workday)",
      "Testing frameworks (unit, integration, performance)",
      "Observability tooling (logging, metrics, tracing)",
    ],
  },
  {
    id: "yield",
    label: "Yield/You Ask",
    icon: "ChatBubbleLeftRightIcon",
    headline: "Yield: What Outcomes Do You Want? What Questions Do You Have?",
    bullets: [
      "Faster go-live? (reduce implementation time)",
      "Lower TCO? (reduce ongoing support costs)",
      "Better observability? (real-time monitoring, proactive alerts)",
      "What are your top 3 concerns? (we'll address them)",
    ],
  },
];

export default function CIDIFLYAccordion() {
  const [openStep, setOpenStep] = useState<string | null>("context");

  return (
    <div className="space-y-4">
      {steps.map((step) => (
        <div key={step.id} className="glass-card rounded-2xl overflow-hidden">
          {/* Header */}
          <button
            onClick={() => setOpenStep(openStep === step.id ? null : step.id)}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-4">
              <Icon name={step.icon as any} size={32} className="text-blue-400" />
              <span className="text-xl font-semibold text-white">
                {step.label}
              </span>
            </div>
            <Icon
              name={openStep === step.id ? "ChevronUpIcon" : "ChevronDownIcon"}
              size={24}
              className="text-neutral-400"
            />
          </button>

          {/* Body */}
          {openStep === step.id && (
            <div className="px-6 pb-6 border-t border-white/5">
              <h4 className="text-lg font-semibold text-white mb-4 mt-4">
                {step.headline}
              </h4>
              <ul className="space-y-3 mb-6">
                {step.bullets.map((bullet, index) => (
                  <li key={`bullet_${index}`} className="flex items-start gap-3">
                    <span className="text-blue-400 mt-1">•</span>
                    <span className="text-neutral-300">{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="w-full h-48 bg-neutral-800/50 border border-white/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-neutral-500 text-sm">
                  [Diagram: {step.label} Flow]
                </span>
              </div>
              <button className="text-blue-400 text-sm font-medium hover:underline">
                Request detailed example →
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}